import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useKeyboardOpen } from '../../hooks/useKeyboardOpen';
import { useTelegramBack } from '../../hooks/useTelegramBack';
import { cn } from '../../lib/cn';
import { stepFor } from '../../lib/layout';
import { DevBack } from './DevBack';
import { StepBar } from './StepBar';

/** The step's bottom slot: undefined outside a StepLayout, null until it is mounted. */
const SlotContext = createContext<HTMLElement | null | undefined>(undefined);

export interface StepLayoutProps {
  title: string;
  /** Where Telegram's back button leads (stepBack). */
  back: string;
  /** Returns false to stay (e.g. to ask about unsaved changes first). */
  guard?: () => boolean;
  /** A carousel has no «Озвучка» step. */
  carousel?: boolean;
  children: ReactNode;
}

/**
 * A step of the path (spec 5.1): title, step bar, a bottom slot for the main
 * button and Telegram's native back button. No menu here.
 */
export function StepLayout({ title, back, guard, carousel, children }: StepLayoutProps) {
  const { pathname } = useLocation();
  const step = stepFor(pathname);
  const onBack = useTelegramBack(back, guard);
  const keyboardOpen = useKeyboardOpen();
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  // The bottom slot grows with what a screen puts above its button («Итого», the balance):
  // the page keeps room for all of it, so its end never hides under the bar.
  const [slotHeight, setSlotHeight] = useState(0);
  useEffect(() => {
    if (!slot || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setSlotHeight(slot.offsetHeight));
    observer.observe(slot);
    return () => observer.disconnect();
  }, [slot]);

  return (
    <SlotContext.Provider value={slot}>
      <div className="safe-top">
        <DevBack onBack={onBack} />
        <h1 className="text-xl font-extrabold px-4 pt-3 max-w-lg mx-auto">{title}</h1>
        {step && <StepBar current={step} carousel={carousel} />}
        <main
          className="px-4 max-w-lg mx-auto pb-[calc(104px+env(safe-area-inset-bottom))]"
          style={slotHeight > 88 ? { paddingBottom: slotHeight + 16 } : undefined}
        >
          {children}
        </main>
        <div
          ref={setSlot}
          className={cn(
            'fixed bottom-0 inset-x-0 mx-auto max-w-lg z-30 bg-canvas/95 px-4 pt-2 pb-[max(12px,env(safe-area-inset-bottom))] empty:hidden',
            keyboardOpen && 'hidden',
          )}
        />
      </div>
    </SlotContext.Provider>
  );
}

/**
 * The step's main button: rendered at the bottom of the screen, while the
 * component that owns it keeps its state. Inline when there is no step layout.
 */
export function StepAction({ children }: { children: ReactNode }) {
  const slot = useContext(SlotContext);
  if (slot === undefined) return <>{children}</>;
  if (slot === null) return null;
  return createPortal(children, slot);
}
